import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Table,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from "@tabler/icons";
import _ from "lodash";
import RemoteSelect2 from "./remoteSelect2";
import { useFormatter } from "../hooks/useFomatter";
import { idOf, rankOptions } from "../helpers/promotionProgress";

const CONDITION_TYPES = [
  { value: "POINT_BALANCE", label: "Point balance" },
  { value: "DIRECT_ACTIVE_DOWNLINE", label: "Direct downline" },
];

const emptyRank = (index) => ({
  rankNumber: index,
  code: "",
  name: "",
  cname: "",
  overridingPlan: null,
  conditionLogic: "AND",
  conditions: [],
});

const emptyCondition = (type = "POINT_BALANCE") =>
  type === "DIRECT_ACTIVE_DOWNLINE"
    ? { type, minCount: 1, minRankCode: "" }
    : { type, pointType: null, minAmount: 0 };

const GtePrefix = () => (
  <Text size="sm" color="dimmed">
    &gt;=
  </Text>
);

const rankLabel = (rank) => rank?.code || "New rank";

const PromotionRankEditor = ({ form: mainForm, name = "ranks" }) => {
  const [formatter] = useFormatter();
  const ranks = _.get(mainForm.values, name) || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const rankSelectData = useMemo(
    () => rankOptions({ ranks }),
    [ranks.map((rank) => `${rank.code}:${rank.name}`).join("|")]
  );

  useEffect(() => {
    if (!ranks.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex > ranks.length - 1) {
      setActiveIndex(ranks.length - 1);
    }
  }, [ranks.length, activeIndex]);

  const updateForm = (next) => {
    mainForm.setFieldValue(
      name,
      next.map((rank, index) => ({ ...rank, rankNumber: index }))
    );
  };

  const handleChange = (index, field, value) => {
    updateForm(
      ranks.map((rank, rowIndex) =>
        rowIndex === index ? { ...rank, [field]: value } : rank
      )
    );
  };

  const handleConditionChange = (rankIndex, conditionIndex, field, value) => {
    const nextConditions = (ranks[rankIndex].conditions || []).map(
      (condition, index) =>
        index === conditionIndex ? { ...condition, [field]: value } : condition
    );
    handleChange(rankIndex, "conditions", nextConditions);
  };

  const handleConditionTypeChange = (rankIndex, conditionIndex, type) => {
    const nextConditions = (ranks[rankIndex].conditions || []).map(
      (condition, index) =>
        index === conditionIndex ? emptyCondition(type) : condition
    );
    handleChange(rankIndex, "conditions", nextConditions);
  };

  const handleAddCondition = (rankIndex) => {
    handleChange(rankIndex, "conditions", [
      ...(ranks[rankIndex].conditions || []),
      emptyCondition("POINT_BALANCE"),
    ]);
  };

  const handleRemoveCondition = (rankIndex, conditionIndex) => {
    handleChange(
      rankIndex,
      "conditions",
      (ranks[rankIndex].conditions || []).filter(
        (_, index) => index !== conditionIndex
      )
    );
  };

  const handleAdd = () => {
    updateForm([...ranks, emptyRank(ranks.length)]);
    setActiveIndex(ranks.length);
  };

  const handleRemove = (index) => {
    updateForm(ranks.filter((_, rowIndex) => rowIndex !== index));
    setActiveIndex((current) => {
      if (index < current) return current - 1;
      if (index === current) return Math.max(0, current - 1);
      return current;
    });
  };

  const handleMove = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= ranks.length) return;
    const next = [...ranks];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    updateForm(next);
    setActiveIndex(target);
  };

  const index = Math.min(activeIndex, Math.max(ranks.length - 1, 0));
  const rank = ranks[index];
  const isTop = rank ? index === ranks.length - 1 : false;
  const nextRank = ranks[index + 1];
  const logic = rank?.conditionLogic === "OR" ? "OR" : "AND";
  const conditions = rank?.conditions || [];

  return (
    <Group align="stretch" noWrap spacing={0}>
      <Box
        sx={(theme) => ({
          width: 220,
          flexShrink: 0,
          borderRight: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[3]
          }`,
          paddingRight: theme.spacing.md,
        })}
      >
        <Tabs
          orientation="vertical"
          value={ranks.length ? String(index) : null}
          onTabChange={(value) => {
            if (value != null) setActiveIndex(Number(value));
          }}
          styles={{
            root: { width: "100%" },
            tabsList: { width: "100%" },
            tab: {
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "8px 10px",
              height: "auto",
            },
          }}
        >
          <Tabs.List>
            {ranks.map((item, rowIndex) => (
              <Tabs.Tab key={item._id || rowIndex} value={String(rowIndex)}>
                <Box>
                  <Text size="sm" weight={600} lineClamp={1}>
                    {rankLabel(item)}
                  </Text>
                  {item.name ? (
                    <Text size="xs" color="dimmed" lineClamp={1}>
                      {item.name}
                    </Text>
                  ) : null}
                </Box>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Button
          variant="light"
          size="xs"
          fullWidth
          mt="md"
          leftIcon={<IconPlus size={14} />}
          onClick={handleAdd}
        >
          Add Rank
        </Button>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, paddingLeft: 24 }}>
        {!rank ? (
          <Text size="sm" color="dimmed">
            Add a rank to start the ladder.
          </Text>
        ) : (
          <>
            <Group position="apart" mb="md" noWrap>
              <div>
                <Group spacing="xs">
                  <Text weight={600}>
                    {rankLabel(rank)}
                    {rank.name ? `  ${rank.name}` : ""}
                  </Text>
                  {isTop ? (
                    <Badge color="grape" size="xs" variant="light">
                      Top rank
                    </Badge>
                  ) : null}
                </Group>
                {!isTop && nextRank ? (
                  <Text size="xs" color="dimmed">
                    Next: {rankLabel(nextRank)}
                    {nextRank.name ? ` ${nextRank.name}` : ""}
                  </Text>
                ) : null}
              </div>
              <Group spacing={4} noWrap>
                <Tooltip label="Move up" withArrow>
                  <span>
                    <ActionIcon
                      variant="subtle"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                    >
                      <IconArrowUp size={16} />
                    </ActionIcon>
                  </span>
                </Tooltip>
                <Tooltip label="Move down" withArrow>
                  <span>
                    <ActionIcon
                      variant="subtle"
                      disabled={isTop}
                      onClick={() => handleMove(index, 1)}
                    >
                      <IconArrowDown size={16} />
                    </ActionIcon>
                  </span>
                </Tooltip>
                <Tooltip label="Delete rank" withArrow>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => handleRemove(index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            <SimpleGrid cols={3} spacing="md">
              <TextInput
                label="Code"
                required
                value={rank.code || ""}
                onChange={(event) =>
                  handleChange(index, "code", event.currentTarget.value)
                }
              />
              <TextInput
                label="Name (Eng)"
                value={rank.name || ""}
                onChange={(event) =>
                  handleChange(index, "name", event.currentTarget.value)
                }
              />
              <TextInput
                label="Name (Chi)"
                value={rank.cname || ""}
                onChange={(event) =>
                  handleChange(index, "cname", event.currentTarget.value)
                }
              />
            </SimpleGrid>

            <Box mt="md">
              <RemoteSelect2
                form={mainForm}
                name={`${name}.${index}.overridingPlan`}
                label="Overriding Plan"
                apiEntity="commissionOverridingPlan"
                valueField="_id"
                labelField="name"
                searchFields={["name", "desc"]}
                clearable
                sort={{ by: "name", order: "asc" }}
              />
            </Box>

            {isTop ? (
              <Text size="sm" color="dimmed" mt="lg">
                Top rank has no promotion condition.
              </Text>
            ) : (
              <>
                <Divider my="lg" />
                <Group position="apart" mb="xs" noWrap>
                  <div>
                    <Text size="sm" weight={600}>
                      Promotion Condition
                    </Text>
                    <Text size="xs" color="dimmed">
                      {logic === "AND"
                        ? "All rows must pass to promote."
                        : "Any one row is enough to promote."}
                    </Text>
                  </div>
                  <SegmentedControl
                    size="xs"
                    value={logic}
                    onChange={(value) =>
                      handleChange(index, "conditionLogic", value)
                    }
                    data={[
                      { value: "AND", label: "AND" },
                      { value: "OR", label: "OR" },
                    ]}
                  />
                </Group>
                <Table
                  verticalSpacing="sm"
                  sx={{
                    "& th, & td": { paddingLeft: 0, paddingRight: 8 },
                    "& th:last-child, & td:last-child": {
                      paddingRight: 0,
                      width: 36,
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 220 }}>Type</th>
                      <th>Requirement</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {conditions.map((condition, conditionIndex) => (
                      <tr key={conditionIndex}>
                        <td>
                          <Select
                            data={CONDITION_TYPES}
                            value={condition.type}
                            onChange={(value) =>
                              handleConditionTypeChange(
                                index,
                                conditionIndex,
                                value
                              )
                            }
                          />
                        </td>
                        <td>
                          {condition.type === "POINT_BALANCE" ? (
                            <Group spacing="xs" noWrap>
                              <Box sx={{ width: 240, flexShrink: 0 }}>
                                <RemoteSelect2
                                  form={mainForm}
                                  apiEntity="pointType"
                                  valueField="_id"
                                  labelField="name"
                                  labelRender={(data) =>
                                    `${data.code || ""} ${data.name || ""}`.trim()
                                  }
                                  searchFields={["code", "name", "cname"]}
                                  placeholder="Point Type"
                                  value={idOf(condition.pointType) || null}
                                  onDataChange={(data) =>
                                    handleConditionChange(
                                      index,
                                      conditionIndex,
                                      "pointType",
                                      data
                                        ? {
                                            _id: data._id,
                                            code: data.code,
                                            name: data.name,
                                            cname: data.cname,
                                          }
                                        : null
                                    )
                                  }
                                  clearable
                                />
                              </Box>
                              <NumberInput
                                min={0}
                                hideControls
                                icon={<GtePrefix />}
                                iconWidth={36}
                                parser={formatter.numberParser}
                                formatter={formatter.numberFormat}
                                value={condition.minAmount}
                                onChange={(value) =>
                                  handleConditionChange(
                                    index,
                                    conditionIndex,
                                    "minAmount",
                                    value
                                  )
                                }
                                sx={{ flex: 1 }}
                              />
                            </Group>
                          ) : (
                            <Group spacing="xs" noWrap>
                              <NumberInput
                                min={1}
                                hideControls
                                icon={<GtePrefix />}
                                iconWidth={36}
                                value={condition.minCount}
                                onChange={(value) =>
                                  handleConditionChange(
                                    index,
                                    conditionIndex,
                                    "minCount",
                                    value
                                  )
                                }
                                sx={{ width: 120 }}
                              />
                              <Text size="sm" color="dimmed">
                                at
                              </Text>
                              <Select
                                placeholder="Rank"
                                data={rankSelectData}
                                value={condition.minRankCode || null}
                                onChange={(value) =>
                                  handleConditionChange(
                                    index,
                                    conditionIndex,
                                    "minRankCode",
                                    value
                                  )
                                }
                                searchable
                                clearable
                                sx={{ flex: 1 }}
                              />
                              <Text size="sm" color="dimmed">
                                or above
                              </Text>
                            </Group>
                          )}
                        </td>
                        <td>
                          <Tooltip label="Delete condition" withArrow>
                            <ActionIcon
                              variant="subtle"
                              onClick={() =>
                                handleRemoveCondition(index, conditionIndex)
                              }
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                    {!conditions.length && (
                      <tr>
                        <td colSpan={3}>
                          <Text size="sm" color="dimmed">
                            No condition. Add one, or leave empty to always
                            qualify for the next rank.
                          </Text>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <Button
                  size="xs"
                  variant="light"
                  mt="sm"
                  leftIcon={<IconPlus size={14} />}
                  onClick={() => handleAddCondition(index)}
                >
                  Add condition
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </Group>
  );
};

export default PromotionRankEditor;
